import { useCallback, useState } from 'react';

// types
import { TableHeader } from '../components/ui/Table/Table';

// constants
const STORAGE_PREFIX = 'bldt-col-prefs';

/**
 * Build a localStorage key scoped to <tableId>.
 * Format: "bldt-col-prefs:<tableId>"
 */
const storageKey = (tableId: string) => `${STORAGE_PREFIX}:${tableId}`;

/**
 * Read saved hidden-column set from localStorage.
 * Returns `null` when nothing has been saved yet (= use defaults).
 */
const readHidden = (tableId: string): Set<string> | null => {
	try {
		const raw = localStorage.getItem(storageKey(tableId));
		if (!raw) return null;
		const parsed: string[] = JSON.parse(raw);
		return new Set(parsed);
	} catch {
		return null;
	}
};

/**
 * Persist hidden-column set to localStorage.
 */
const writeHidden = (tableId: string, hidden: Set<string>) => {
	try {
		localStorage.setItem(storageKey(tableId), JSON.stringify([...hidden]));
	} catch {
		// quota exceeded – silently ignore
	}
};

export interface UseColumnPreferencesReturn {
	/** Headers filtered to only visible columns */
	visibleHeaders: TableHeader[];
	/** Full list of all headers with their visibility state */
	allHeaders: { header: TableHeader; visible: boolean }[];
	/** Toggle a single column's visibility */
	toggleColumn: (value: string) => void;
	/** Reset to default visibility (remove localStorage entry) */
	resetToDefaults: () => void;
	/** Whether preferences have been customised */
	isCustomised: boolean;
}

/**
 * Hook that manages per-table column visibility preferences in localStorage.
 *
 * @param tableId  Unique identifier for this table instance (e.g. "transactions", "entities").
 * @param headers  The full set of column headers the table supports.
 */
export function useColumnPreferences(
	tableId: string | undefined,
	headers: TableHeader[]
): UseColumnPreferencesReturn {
	// Initialise state from localStorage (or null → all visible)
	const [hidden, setHidden] = useState<Set<string>>(() => {
		if (!tableId) return new Set<string>();
		return readHidden(tableId) ?? new Set<string>();
	});

	const isCustomised = tableId ? readHidden(tableId) !== null : false;

	const toggleColumn = useCallback(
		(value: string) => {
			if (!tableId) return;
			setHidden((prev) => {
				const next = new Set(prev);
				if (next.has(value)) {
					next.delete(value);
				} else {
					// Don't allow hiding ALL columns – keep at least one visible
					const visibleCount = headers.filter((h) => !next.has(h.value)).length;
					if (visibleCount <= 1) return prev;
					next.add(value);
				}
				writeHidden(tableId, next);
				return next;
			});
		},
		[tableId, headers]
	);

	const resetToDefaults = useCallback(() => {
		if (!tableId) return;
		try {
			localStorage.removeItem(storageKey(tableId));
		} catch {
			// ignore
		}
		setHidden(new Set<string>());
	}, [tableId]);

	const visibleHeaders = tableId ? headers.filter((h) => !hidden.has(h.value)) : headers;

	const allHeaders = headers.map((header) => ({
		header,
		visible: !hidden.has(header.value),
	}));

	return {
		visibleHeaders,
		allHeaders,
		toggleColumn,
		resetToDefaults,
		isCustomised,
	};
}
